import React, { useEffect, useState } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { linkProps } from '../../../utils/appLink';
import client from '../../../utils/contentfulClient';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const CARD_COUNT = 4;

const formatBlogDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const mapPostToCard = (post) => {
  const { slug, category, heading, blurb, blogDate, headingImage } =
    post.fields;
  const cleanSlug = slug.replace(/\/$/, '');
  const link = cleanSlug.includes('/')
    ? `/blog/${slug}`
    : `/blog/${category}/${slug}`;

  return {
    image: headingImage?.fields?.file?.url
      ? `https:${headingImage.fields.file.url}`
      : '',
    date: formatBlogDate(blogDate),
    title: heading,
    body: blurb,
    link,
  };
};

const FourBlogCardsComponent = ({ data, tag }) => {
  const { title, subtitle, description, footer } = data;
  const resolvedSubtitle = subtitle || description || '';
  const [cards, setCards] = useState([]);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const taggedResponse = await client.getEntries({
          content_type: 'blogPost',
          'fields.tags[in]': tag,
          order: '-fields.blogDate',
          limit: CARD_COUNT,
        });

        const taggedPosts = taggedResponse.items;
        let fetched = taggedPosts.map(mapPostToCard);

        if (fetched.length < CARD_COUNT) {
          const needed = CARD_COUNT - fetched.length;
          const excludeIds = new Set(taggedPosts.map((p) => p.sys.id));
          const fallbackResponse = await client.getEntries({
            content_type: 'blogPost',
            'fields.category': 'insurance',
            order: '-fields.blogDate',
            limit: 10,
          });

          const fallbackCards = fallbackResponse.items
            .filter((p) => !excludeIds.has(p.sys.id))
            .slice(0, needed)
            .map(mapPostToCard);

          fetched = [...fetched, ...fallbackCards];
        }

        setCards(fetched);
      } catch (err) {
        // ignore
      }
    };

    fetchCards();
  }, [tag]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '1216px',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        <ScrollFadeWrapper variant="text">
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              maxWidth: '980px',
              mx: 'auto',
              mb: '40px',
            }}
          >
            {resolvedSubtitle}
          </Typography>
        </ScrollFadeWrapper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 286px)',
            gap: '24px',
            justifyContent: 'center',
            mb: '40px',
          }}
        >
          {cards.map((card, index) => (
            <ScrollFadeWrapper key={index} delay={index * 0.15}>
              <Box
                sx={{
                  width: '286px',
                  height: '418px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '32px',
                  boxShadow:
                    '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Image
                  width={286}
                  height={418}
                  src={card.image}
                  alt={card.title}
                  sx={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    backgroundColor: '#A2ACB1',
                  }}
                />

                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    pt: '32px',
                    pb: '32px',
                  }}
                >
                  <Box sx={{ px: '24px' }}>
                    <Typography
                      component={'p'}
                      sx={{
                        fontFamily: 'Overline, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: '1.25rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        textAlign: 'right',
                        mb: '12px',
                      }}
                    >
                      {card.date}
                    </Typography>

                    <Typography
                      component={'h6'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                        mb: '12px',
                        textAlign: 'left',
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'right', px: '24px' }}>
                    <Link
                      {...linkProps(card.link)}
                      underline="none"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        verticalAlign: 'middle',
                        '&:hover': {
                          color: '#9EA900',
                        },
                      }}
                    >
                      Read More
                    </Link>
                  </Box>
                </Box>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>

        <Typography
          component={'p'}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            maxWidth: '596px',
            mx: 'auto',
          }}
        >
          {footer}
        </Typography>
      </Box>
    </Box>
  );
};

export default FourBlogCardsComponent;
